import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Switch,
  TextField,
} from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import ContentCopyIcon from '@material-ui/icons/FileCopyOutlined';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  disableAkadmyWidget,
  enableAkadmyWidget,
  getAkadmyWidgetConfig,
  getAllAkademySchools,
  getSchoolById,
  updateAkadmyWidgetConfig,
} from '../AkademySlice';
import BuzzSelect from '../../../components/BuzzSelect/BuzzSelect';
import { Alert } from '@material-ui/lab';
import { API_BASE_BUZZONE } from '../../../environments';
import { copyTextToClipboard } from '../../../utils/helpers';
import BuzzAutoCompleteSingleSelectInput from '../../../components/BuzzAutoCompleteInput/BuzzAutoCompleteSingleSelectInput';
import integrationOptions from './integrationOptions';

const authenticationOptions = [
  {
    id: 'INTERNAL',
    label: 'BuzzOne',
    description:
      'The widget manages authentication internally. Best for websites where seamless user login is needed within the widget itself.',
  },
  {
    id: 'EXTERNAL',
    label: 'Third Party',
    description:
      'The widget does not handle authentication. The app that integrates, manages it instead. Ideal for mobile / web apps, or any setup where the app controls the auth flow.',
  },
];

function AkademyWidgetSetup() {
  const dispatch = useDispatch();

  const { school, schoolList, widgetConfig } = useSelector(
    ({ akademyReducer }) => {
      return {
        school: akademyReducer.school,
        schoolList: akademyReducer.schools,
        widgetConfig: akademyReducer.widgetConfig,
      };
    }
  );

  useEffect(() => {
    dispatch(getAllAkademySchools());
  }, []);

  const onChangeSchool = (id) => {
    if (!id) return;

    dispatch(getSchoolById({ id }));
    dispatch(getAkadmyWidgetConfig({ schoolId: id }));
  };

  const handleEnableWidget = () => {
    dispatch(enableAkadmyWidget({ schoolId: school.data._id }));
  };

  const handleDisableWidget = () => {
    const isConfirmed = window.confirm(
      `Are you sure want to disable the widget for ${school.data.name} ?`
    );
    if (!isConfirmed) return;

    dispatch(disableAkadmyWidget({ schoolId: school.data._id }));
  };

  const [selectedIntegrationId, setSelectedIntegrationId] = useState(
    integrationOptions[0].id
  );
  const onChangeIntegrationMethod = (id) => setSelectedIntegrationId(() => id);

  const selectedIntegration = useMemo(() => {
    if (!selectedIntegrationId) return;

    const integration = integrationOptions.find(
      (x) => x.id === selectedIntegrationId
    );

    integration.code =
      integration.codeConfig[
        widgetConfig.data && widgetConfig.data.authMode === 'INTERNAL'
          ? 'internalAuth'
          : 'externalAuth'
      ];

    integration.code = integration.code
      .replaceAll('[[BUZZONE_URL]]', API_BASE_BUZZONE)
      .replaceAll('[[USER_EMAIL]]', 'dumbeldore@gmail.com');

    if (widgetConfig.data)
      integration.code = integration.code.replaceAll(
        '[[APP_ID]]',
        widgetConfig.data.appId
      );

    return integration;
  }, [selectedIntegrationId, widgetConfig]);

  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const handleCopyCode = async () => {
    await copyTextToClipboard(selectedIntegration.code);
    setIsCodeCopied(true);
  };

  useEffect(() => {
    if (!isCodeCopied) return;

    const timeoutId = setTimeout(() => {
      setIsCodeCopied(false);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [isCodeCopied]);

  const updateConfig = async (data) => {
    return await dispatch(
      updateAkadmyWidgetConfig({
        schoolId: school.data._id,
        ...data,
      })
    ).unwrap();
  };

  const [originText, setOriginText] = useState('');
  const handleAddOrigin = async () => {
    await updateConfig({
      allowedOrigins: [...widgetConfig.data.allowedOrigins, originText],
    });
    setOriginText('');
  };

  const handleRemoveOrigin = (originToRemove) => {
    const isConfirmed = window.confirm('Are you sure want to remove?');
    if (!isConfirmed) return;

    updateConfig({
      allowedOrigins: widgetConfig.data.allowedOrigins.filter(
        (origin) => origin !== originToRemove
      ),
    });
  };

  return (
    <Box>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Typography variant="h6">Widget Setup</Typography>
        <Box style={{ minWidth: '300px', maxWidth: '700px' }}>
          {!school.data && (
            <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
              Select a school
            </Typography>
          )}
          <BuzzAutoCompleteSingleSelectInput
            options={schoolList.map(({ _id, name }) => ({
              label: name,
              value: _id,
            }))}
            value={school.data && school.data._id}
            onChange={(value) => onChangeSchool(value)}
          />
        </Box>
      </Box>

      {/* {school.data && (
        <Box>
          <Typography variant="h7" style={{ fontWeight: 400 }}>
            {school.data.name}
          </Typography>
          <Typography
            variant="subtitle1"
            style={{ fontSize: '0.85rem' }}
            color="textSecondary">
            {schoolId}
          </Typography>
        </Box>
      )} */}

      {/*  && !widgetConfig.data.isEnabled  */}

      {school.isError ? (
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          style={{ height: '600px' }}>
          <Typography variant="subtitle1" color="textSecondary">
            School not found
          </Typography>
        </Box>
      ) : (!widgetConfig.data && widgetConfig.isError) ||
        (widgetConfig.data && !widgetConfig.data.isEnabled) ? (
        <Box
          mt={2}
          p={2}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F5F5F566',
          }}>
          <Box py={2}>
            <Typography variant="subtitle1">
              Enable aKadmy widget for this school
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              The widget is currently disabled. Enable it to generate
              integration code.
            </Typography>
          </Box>
          <Switch
            checked={false}
            onChange={handleEnableWidget}
            color="primary"
          />
        </Box>
      ) : (
        widgetConfig.data && (
          <>
            <Box
              mt={2}
              p={2}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              style={{
                backgroundColor: '#07BC0C0D',
              }}>
              <Box display={'flex'} alignItems={'center'} py={2}>
                <DoneIcon style={{ fill: '#07BC0C', fontSize: 30 }} />
                <Typography
                  style={{
                    color: '#07BC0C',
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    marginLeft: '1rem',
                  }}>
                  Widget Enabled
                </Typography>
              </Box>
              <Switch
                checked={true}
                onChange={handleDisableWidget}
                color="success"
                style={{ color: '#07BC0C' }}
              />
            </Box>

            <Box mt={2}>
              <Typography>School App ID</Typography>
              <Box
                p={2}
                style={{
                  backgroundColor: '#F5F5F566',
                }}>
                <Typography
                  variant="h6"
                  color="primary"
                  style={{ fontSize: '1rem' }}>
                  {widgetConfig.data.appId}
                </Typography>
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '0.9rem' }}>
                  This App ID uniquely identifies the school. It is
                  automatically generated and cannot be changed.
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Typography>Authentication Method</Typography>
              <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
                Choose how you want to handle the user authentication.
              </Typography>
              <Box mt={2} style={{ minWidth: '100px', maxWidth: '300px' }}>
                <BuzzSelect
                  size="small"
                  options={authenticationOptions.map(({ id, label }) => ({
                    label,
                    value: id,
                  }))}
                  value={widgetConfig.data.authMode}
                  onChange={(e) => updateConfig({ authMode: e.target.value })}
                />
              </Box>
              <Box my={2}>
                <Typography>Available Options:</Typography>
                <Box py={2} maxWidth={'75%'}>
                  {authenticationOptions.map(({ id, label, description }) => (
                    <Typography
                      key={id}
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.9rem',
                      }}>
                      <strong>{label}</strong> - {description}
                    </Typography>
                  ))}
                </Box>

                <Alert severity="info">
                  <Typography>
                    <strong>Note</strong> : The Integration code slightly varies
                    based on the selected authentication method.
                  </Typography>
                </Alert>
              </Box>
            </Box>

            <Box mt={2}>
              <Typography variant="h6" style={{ fontSize: '1rem' }}>
                Integration Code
              </Typography>
              <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
                Choose where you want to embed the widget
              </Typography>
              <Box mt={2} style={{ minWidth: '100px', maxWidth: '300px' }}>
                <BuzzSelect
                  size="small"
                  options={integrationOptions.map(({ id, title }) => ({
                    label: title,
                    value: id,
                  }))}
                  value={selectedIntegrationId}
                  onChange={(e) => onChangeIntegrationMethod(e.target.value)}
                />
              </Box>
            </Box>

            <Box mt={2}>
              <Typography>{selectedIntegration.title} Integration</Typography>
              <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
                Use this code to embed the widget on your{' '}
                {selectedIntegration.title}
              </Typography>

              <Box
                my={2}
                p={2}
                style={{
                  backgroundColor: '#F5F5F566',
                  position: 'relative',
                  minHeight: '100px',
                }}>
                <Button
                  variant="outlined"
                  onClick={handleCopyCode}
                  startIcon={
                    isCodeCopied ? (
                      <DoneIcon style={{ fill: 'black' }} />
                    ) : (
                      <ContentCopyIcon />
                    )
                  }
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    width: '130px',
                    backgroundColor: isCodeCopied ? '#22c55e' : 'white',
                  }}>
                  {isCodeCopied ? 'Copied' : 'Copy Code'}
                </Button>
                <Typography
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    whiteSpace: 'pre-wrap',
                    maxWidth: '80%',
                  }}>
                  {selectedIntegration.code}
                </Typography>
              </Box>

              {selectedIntegration.instructions && (
                <Box my={2}>
                  <Typography>Instructions</Typography>
                  <Box py={2} style={{ backgroundColor: '#F5F5F566' }}>
                    <ol>
                      {selectedIntegration.instructions.map(
                        (instruction, idx) => (
                          <li key={idx}>
                            <Typography style={{ fontSize: '0.9rem' }}>
                              {instruction}
                            </Typography>{' '}
                          </li>
                        )
                      )}
                    </ol>
                  </Box>
                </Box>
              )}

              <Alert severity="info">
                <Typography>
                  <strong>Note</strong> : App ID is fixed for your school. User
                  email is that of the logged-In user.
                </Typography>
              </Alert>
            </Box>

            <Box mt={2}>
              <Typography variant="h6" style={{ fontSize: '1rem' }}>
                Allowed Webites
              </Typography>
              <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
                Specify which websites are allowed to load this widget. &#40;
                Applicable only for websites / webapps &#41;
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography>Allowed Website URLs</Typography>
              <Box my={1} display="flex" alignItems="center">
                <TextField
                  variant="outlined"
                  placeholder="https://teenybeans.in/"
                  value={originText}
                  onChange={(e) => setOriginText(e.target.value)}
                  size="small"
                  fullWidth
                  style={{ maxWidth: '500px' }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: '10px' }}
                  onClick={handleAddOrigin}
                  disabled={
                    !originText ||
                    widgetConfig.data.allowedOrigins.includes(originText)
                  }>
                  Add {widgetConfig.data.allowedOrigins.length > 0 && 'Another'}
                </Button>
              </Box>

              <Box
                my={2}
                p={2}
                style={{
                  backgroundColor: '#F5F5F566',
                }}>
                {widgetConfig.data.allowedOrigins.length === 0 ? (
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                    No websites added yet. Add your first website URL above.
                  </Typography>
                ) : (
                  widgetConfig.data.allowedOrigins.map((origin) => (
                    <Box
                      display={'flex'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      style={{
                        margin: '10px 0',
                        padding: '10px',
                        borderRadius: '5px',
                        border: '1px solid #DFDFDF',
                        backgroundColor: 'white',
                      }}>
                      <Typography>{origin}</Typography>
                      <IconButton
                        color="primary"
                        style={{ padding: 0 }}
                        onClick={() => handleRemoveOrigin(origin)}>
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Box>

              <Typography color="textSecondary" style={{ fontSize: '0.9rem' }}>
                Only these websites will be able to display the widget.
              </Typography>
            </Box>
          </>
        )
      )}
    </Box>
  );
}

export default AkademyWidgetSetup;
